export type EnginePhase = 'idle' | 'debouncing' | 'flushing';

export interface EngineSnapshot {
  phase: EnginePhase;
  lastRound: 'none' | 'success' | 'error';
}

export interface AutosaveEngine {
  /** A draft changed — (re)arm the debounce, or queue a rerun behind the in-flight round. */
  notifyChange: () => void;
  /** Cancel the pending debounce (and any queued rerun) and await the in-flight round. */
  settle: () => Promise<void>;
  /**
   * While suspended (Weiter's own mutation is in flight) no round may start —
   * a concurrent flush would classify against the baseline Weiter has not yet
   * cleared and double-send the delta. Changes made while suspended re-arm
   * the debounce on resume.
   */
  setSuspended: (suspended: boolean) => void;
  dispose: () => void;
}

/**
 * The autosave scheduler as a plain state machine — debounce, single-flight,
 * rerun-after-round, suspension — with React kept outside: the hook feeds it
 * a round runner that reads a latest-ref and mirrors snapshots into state.
 * `runFlushRound` must never reject; it returns false for a failed round.
 */
export function createAutosaveEngine(options: {
  runFlushRound: () => Promise<boolean>;
  onChange: (snapshot: EngineSnapshot) => void;
  debounceMs: number;
}): AutosaveEngine {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let rerunRequested = false;
  let suspended = false;
  let deferredWhileSuspended = false;
  let phase: EnginePhase = 'idle';
  let lastRound: EngineSnapshot['lastRound'] = 'none';

  const emit = () => options.onChange({ phase, lastRound });
  const cancelTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const startRound = (): void => {
    if (inFlight) {
      rerunRequested = true;
      return;
    }
    phase = 'flushing';
    emit();
    inFlight = options
      .runFlushRound()
      .then((succeeded) => {
        lastRound = succeeded ? 'success' : 'error';
      })
      .finally(() => {
        inFlight = null;
        phase = 'idle';
        if (rerunRequested) {
          rerunRequested = false;
          arm();
        } else {
          emit();
        }
      });
  };

  const arm = (): void => {
    if (suspended) {
      deferredWhileSuspended = true;
      return;
    }
    cancelTimer();
    phase = 'debouncing';
    emit();
    timer = setTimeout(() => {
      timer = null;
      startRound();
    }, options.debounceMs);
  };

  return {
    notifyChange: () => {
      // A failed round stops reading as an error the moment the user resumes
      // editing — the retry is this very debounce.
      if (lastRound === 'error') lastRound = 'none';
      if (inFlight) {
        rerunRequested = true;
        emit();
        return;
      }
      arm();
    },

    settle: async () => {
      // Weiter takes over from here — it commits the residual delta itself,
      // so the cancelled debounce and any queued rerun are dropped, not
      // flushed.
      cancelTimer();
      rerunRequested = false;
      deferredWhileSuspended = false;
      if (phase === 'debouncing') {
        phase = 'idle';
        emit();
      }
      if (inFlight) await inFlight;
    },

    setSuspended: (next: boolean) => {
      suspended = next;
      if (next) {
        if (timer !== null) {
          cancelTimer();
          deferredWhileSuspended = true;
          phase = 'idle';
          emit();
        }
      } else if (deferredWhileSuspended) {
        deferredWhileSuspended = false;
        arm();
      }
    },

    dispose: cancelTimer
  };
}
